import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPlay,FiAward,FiUsers,FiTrendingUp}=FiIcons;

const HeroSection=()=> {
  const stats=[
    {icon: FiUsers,value: '2,547+',label: 'Lives Transformed'},
    {icon: FiAward,value: '892+',label: 'Jobs Created'},
    {icon: FiTrendingUp,value: '95%',label: 'Success Rate'}
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 gradient-overlay"></div>
        <img 
          src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="Transforming lives through dignity"
          className="w-full h-full object-cover opacity-40"
        />
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-2 h-2 bg-orange-400 rounded-full float-animation"></div>
        <div className="absolute top-40 right-20 w-3 h-3 bg-blue-400 rounded-full float-animation" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-40 left-20 w-4 h-4 bg-green-400 rounded-full float-animation" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="text-white">
            {/* Trust Badge */}
            <motion.div
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.6}}
              className="flex items-center space-x-3 mb-8"
            >
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <SafeIcon icon={FiAward} className="w-4 h-4 text-orange-400" />
                <span className="text-sm font-medium">Certified 80G NGO</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-sm font-medium">Verified Impact</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.8,delay: 0.2}}
              className="typography-hero text-5xl md:text-7xl mb-6"
            >
              From Streets to
              <span className="block text-transparent bg-clip-text gradient-premium">
                Success Stories
              </span>
            </motion.h1>

            <motion.p
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.8,delay: 0.4}}
              className="typography-body text-xl md:text-2xl text-gray-200 mb-4 max-w-2xl"
            >
              India's leading rehabilitation foundation transforming lives through dignity,skills,and sustainable livelihoods.
            </motion.p>

            <motion.div
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.8,delay: 0.6}}
              className="flex items-center space-x-2 text-orange-200 mb-12"
            >
              <div className="w-12 h-0.5 bg-orange-400"></div>
              <span className="typography-caption">Your ₹500=One Day of Dignity</span>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.8,delay: 0.8}}
              className="flex flex-col sm:flex-row gap-4 mb-16"
            >
              <motion.button
                whileHover={{scale: 1.05,boxShadow: '0 20px 40px rgba(234,88,12,0.3)'}}
                whileTap={{scale: 0.95}}
                className="btn-premium-primary text-lg px-10 py-4"
              >
                Start Donating
              </motion.button>
              
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                className="flex items-center justify-center space-x-3 px-10 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300"
              >
                <SafeIcon icon={FiPlay} className="w-5 h-5" />
                <span>Watch Our Story</span>
              </motion.button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.8,delay: 1}}
              className="grid grid-cols-3 gap-8"
            >
              {stats.map((stat,index)=> (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-2">
                    <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <SafeIcon icon={stat.icon} className="w-5 h-5 text-orange-400" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-gray-300">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Column - Video/Image */}
          <motion.div
            initial={{opacity: 0,x: 50}}
            animate={{opacity: 1,x: 0}}
            transition={{duration: 1,delay: 0.5}}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-premium-lg">
              <img 
                src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Success story"
                className="w-full h-96 lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>

              {/* Play Button Overlay */}
              <motion.button
                whileHover={{scale: 1.1}}
                whileTap={{scale: 0.9}}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center pulse-glow">
                  <SafeIcon icon={FiPlay} className="w-8 h-8 text-orange-600 ml-1" />
                </div>
              </motion.button>

              {/* Achievement Badge */}
              <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">95%</div>
                <div className="text-xs text-gray-600 font-medium">Success Rate</div>
              </div>
            </div>

            {/* Floating Cards */}
            <motion.div
              animate={{y: [0,-10,0]}}
              transition={{repeat: Infinity,duration: 3,delay: 1}}
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-premium"
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">2000+ Lives</div>
                  <div className="text-sm text-gray-600">Transformed</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        transition={{delay: 2}}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{y: [0,10,0]}}
          transition={{repeat: Infinity,duration: 2}}
          className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center"
        >
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;